/* ============================================================
   KPI GLOSSARY
   Rich-tooltip metadata for the small KPI cards at the top of tabs.
   Same shape as statementGlossary — { title?, formula, hint, explanation,
   direction, betterWhen } — so the RichExplanation component renders
   them identically.

   Keyed by normalized label text (toLowerCase + collapse whitespace +
   normalize dashes). Cards whose label comes from JSX (e.g. "DSO +
   EduTip") are handled by their own EduTip; this glossary focuses on
   bare-string KPI labels.
   ============================================================ */

function normalizeKey(label) {
  return String(label || '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/[—–−]/g, '-')
    .trim();
}

const ENTRIES = {
  /* ── Actuals Overview cards ─────────────────────────────────── */
  'net worth (proxy)': {
    formula: 'Liquid + fixed - short-term debt - LT liab',
    hint: 'Snapshot view of the balance sheet.',
    explanation: 'Quick-read net worth: liquid assets + fixed assets at book value, minus short-term debt and long-term liabilities. "Proxy" because it skips suspense/reconciliation entries; for the audited number, use Total equity on the Balance Sheet.',
    direction: { whenUp: 'Getting wealthier.', whenDown: 'Drawing down reserves.' },
    betterWhen: 'higher',
  },
  'liquid assets': {
    formula: 'Σ bank + cash + e-wallets (liquid flag)',
    hint: 'Immediately spendable money.',
    explanation: 'Sum of bank, cash, and e-wallet balances with the liquid flag set. Excludes maintaining balances and foreign-currency dust. This is the runway denominator.',
    betterWhen: 'higher',
  },
  'cc debt': {
    hint: 'Revolving short-term balance owed.',
    explanation: 'Alias for short-term debt: outstanding card, revolving, or similar near-term financing balances.',
    betterWhen: 'lower',
  },
  'credit card debt': {
    hint: 'Revolving short-term balance owed.',
    explanation: 'Outstanding card or revolving balances. Synonymous with the short-term debt KPI.',
    betterWhen: 'lower',
  },
  'cc credits': {
    hint: 'Statement credits on card or revolving accounts.',
    explanation: 'Credits sitting on card or revolving statements (from refunds, rewards, chargebacks). Effectively negative liabilities — they reduce what you will owe next cycle.',
    betterWhen: 'higher',
  },
  'credit card credits': {
    hint: 'Statement credits on card or revolving accounts.',
    explanation: 'Credits on card or revolving statements. Synonymous with short-term credits.',
    betterWhen: 'higher',
  },

  /* ── Assets sub-breakdowns ──────────────────────────────────── */
  'total liquid assets': {
    formula: 'Σ BALANCES.bank + BALANCES.cash (liquid flag)',
    hint: 'Bank + cash + e-wallets.',
    explanation: 'Sum across every liquid account. Matches the "Liquid assets" line on the Balance Sheet.',
    betterWhen: 'higher',
  },
  'total liquid': {
    hint: 'Same as Total liquid assets.',
    explanation: 'Alias for Total liquid assets — a one-line liquid position across all accounts.',
    betterWhen: 'higher',
  },
  'bank balances': {
    hint: 'Bank-account balances only.',
    explanation: 'Sum of bank balances (no e-wallets, no physical cash). Subset of Total liquid.',
    betterWhen: 'higher',
  },
  'bank': {
    hint: 'Bank-account balances.',
    explanation: 'Short label for Bank balances.',
    betterWhen: 'higher',
  },
  'cash': {
    hint: 'Physical cash + e-wallets.',
    explanation: 'Cash in wallet + e-wallet balances (wallets, payment apps, and multicurrency cash apps).',
    betterWhen: 'higher',
  },
  'cash + e-wallets': {
    hint: 'Physical cash + e-wallets.',
    explanation: 'Grouped cash line: wallet, payment apps, and multicurrency cash apps.',
    betterWhen: 'higher',
  },

  /* ── Liabilities sub-breakdowns ─────────────────────────────── */
  'total liabilities': {
    formula: 'Short-term debt - credits + LT liabilities',
    hint: 'All money owed.',
    explanation: 'Sum of every liability category: short-term debt minus statement credits, plus long-term liabilities.',
    betterWhen: 'lower',
  },
  'long-term liabilities': {
    hint: 'Term debt + multi-year loans.',
    explanation: 'Obligations with repayment schedules longer than a year, such as property loans, vehicle loans, or multi-year notes.',
    betterWhen: 'lower',
  },
  'long-term': {
    hint: 'Long-term liabilities.',
    explanation: 'Short label for Long-term liabilities.',
    betterWhen: 'lower',
  },

  /* ── Equity ─────────────────────────────────────────────────── */
  'total equity': {
    formula: 'Total assets − Total liabilities',
    hint: 'Residual claim after debts.',
    explanation: 'Residual balance-sheet claim — assets net of liabilities. Grows as income is retained and debt is paid down; shrinks as debt grows or assets fall.',
    betterWhen: 'higher',
  },
  'suspense balance': {
    hint: 'Unreconciled plug.',
    explanation: 'Residual from imports that didn\'t balance cleanly. Should trend toward zero over time.',
    betterWhen: 'lower',
  },
  'suspense': {
    hint: 'Unreconciled plug.',
    explanation: 'Short label for Suspense balance.',
    betterWhen: 'lower',
  },

  /* ── Budgets: recurring + subs + AR/AP ──────────────────────── */
  'monthly subs': {
    hint: 'Monthly subscription spend.',
    explanation: 'Sum of all subscriptions charged monthly. Core recurring burn.',
    betterWhen: 'lower',
  },
  'monthly subscriptions': {
    hint: 'Monthly subscription spend.',
    explanation: 'Sum of all subscriptions billed monthly (SaaS, streaming, memberships).',
    betterWhen: 'lower',
  },
  'yearly ÷ 12': {
    formula: 'Σ yearly subs ÷ 12',
    hint: 'Annual subs amortized per month.',
    explanation: 'Annual subscriptions divided by 12 — their monthly-equivalent drag.',
    betterWhen: 'lower',
  },
  'yearly subscriptions ÷ 12': {
    formula: 'Σ yearly subs ÷ 12',
    hint: 'Annual subs amortized per month.',
    explanation: 'Annual subscriptions divided by 12 — their monthly-equivalent drag.',
    betterWhen: 'lower',
  },
  'quarterly amort.': {
    formula: 'Σ quarterly subs ÷ 3',
    hint: 'Quarterly subs amortized per month.',
    explanation: 'Quarterly subscriptions divided by 3 — their monthly-equivalent drag.',
    betterWhen: 'lower',
  },
  'quarterly subscriptions amortized': {
    formula: 'Σ quarterly subs ÷ 3',
    hint: 'Quarterly subs amortized per month.',
    explanation: 'Quarterly subscriptions divided by 3 — their monthly-equivalent drag.',
    betterWhen: 'lower',
  },
  'total monthly recurring': {
    formula: 'Monthly + yearly÷12 + quarterly÷3',
    hint: 'Effective monthly recurring outflow.',
    explanation: 'All recurring subscriptions normalized to their monthly-equivalent rate. The true "how much do subs cost me per month" number.',
    betterWhen: 'lower',
  },

  'total monthly ap': {
    hint: 'Monthly support AP obligations.',
    explanation: 'Total monthly support AP for this stream. Settings defaults scale with inflation; individual months can be pinned.',
    betterWhen: 'neutral',
  },
  'total monthly ap (all streams)': {
    hint: 'Monthly support AP across all channels.',
    explanation: 'Sum of monthly support AP across direct + routed-channel streams.',
    betterWhen: 'neutral',
  },
  'largest channel': {
    hint: 'Stream with the highest monthly AP.',
    explanation: 'Whichever AP stream contributes the most spend month-to-month. Useful for prioritization.',
    betterWhen: 'neutral',
  },

  'total expected ar': {
    hint: 'Expected AR for this stream.',
    explanation: 'Expected monthly AR for a contribution stream, after rate adjustments.',
    betterWhen: 'higher',
  },
  'total expected (rate-adj)': {
    hint: 'Rate-adjusted expected AR.',
    explanation: 'Expected AR after applying the collection mode or per-stream override rate.',
    betterWhen: 'higher',
  },
  'total expected (rate-adjusted)': {
    hint: 'Rate-adjusted expected AR.',
    explanation: 'Expected AR after applying the collection mode or per-stream override rate.',
    betterWhen: 'higher',
  },
  'total expected ar (all streams)': {
    hint: 'Expected AR across every contribution stream.',
    explanation: 'Expected AR summed across support-contributor streams.',
    betterWhen: 'higher',
  },
  'total scheduled': {
    hint: 'Scheduled AR per schedule.',
    explanation: 'Contribution-AR scheduled amount per the piecewise editor before rate adjustment.',
    betterWhen: 'neutral',
  },
  'total scheduled education ar': {
    hint: 'Scheduled contribution AR.',
    explanation: 'Contribution-AR scheduled amount from the piecewise editor before rate adjustment.',
    betterWhen: 'neutral',
  },
  'largest stream': {
    hint: 'Stream with the highest expected AR.',
    explanation: 'Whichever AR stream is the biggest contributor. Useful for prioritization and collection effort.',
    betterWhen: 'neutral',
  },

  'arrears': {
    hint: 'Outstanding unpaid routed-channel AR.',
    explanation: 'Expected-minus-received routed-channel AR. Positive = owed, not yet collected.',
    betterWhen: 'lower',
  },
  'routed arrears': {
    hint: 'Outstanding routed-channel AR.',
    explanation: 'Expected-minus-received routed-channel AR over the tracked period. Positive = still owed.',
    betterWhen: 'lower',
  },
  'total observed': {
    hint: 'Routed payments received.',
    explanation: 'Sum of routed payments actually received over the tracked window.',
    betterWhen: 'higher',
  },
  'total routed payments observed': {
    hint: 'Received routed payments.',
    explanation: 'Sum of routed payments observed — the actuals that offset expected AR.',
    betterWhen: 'higher',
  },
  'payments observed': {
    hint: 'Count of tracked payments.',
    explanation: 'Number of routed payments observed over the window (distinct from the amount total).',
    betterWhen: 'higher',
  },

  /* ── Primary income spec ────────────────────────────────────── */
  'hourly': {
    hint: 'Hourly rate.',
    explanation: 'Hourly or unit-rate baseline for the primary-income spec.',
    betterWhen: 'higher',
  },
  'fortnightly gross': {
    formula: 'Hourly × hrs/week × 2',
    hint: 'Gross per fortnight.',
    explanation: 'Fortnightly gross pay before withholding and statutory contributions.',
    betterWhen: 'higher',
  },
  'monthly gross': {
    formula: 'Hourly × hrs/month',
    hint: 'Gross per month.',
    explanation: 'Monthly gross pay before withholding tax and statutory contributions.',
    betterWhen: 'higher',
  },

  /* ── Actuals adjusting-entry telemetry ──────────────────────── */
  'posted entries': {
    hint: 'Adjusting entries posted to books.',
    explanation: 'Count of adjusting JEs marked posted. These flow into every downstream statement.',
    betterWhen: 'neutral',
  },
  'is expense adj.': {
    hint: 'Income-statement impact of adjustments.',
    explanation: 'Net effect of adjusting entries on the income statement (accrued/deferred expense movement).',
    betterWhen: 'neutral',
  },
  'bs liability adj.': {
    hint: 'Balance-sheet liability impact.',
    explanation: 'Net effect of adjusting entries on BS liabilities (accruals / deferrals).',
    betterWhen: 'neutral',
  },
  'bs-affecting': {
    hint: 'Count of BS-touching JEs.',
    explanation: 'How many adjusting entries touch the balance sheet (vs. pure P&L shifts).',
    betterWhen: 'neutral',
  },

  /* ── Comparative Tables KPI timeline labels ─────────────────── */
  'liquid ending': {
    formula: 'Liquid balance at end of period',
    hint: 'Liquid position at period close.',
    explanation: 'Sum of liquid bank + cash + e-wallet balances at the end of each period. The trajectory shows whether cash is accumulating or drawing down.',
    direction: { whenUp: 'Liquidity growing.', whenDown: 'Liquidity drawing down.' },
    betterWhen: 'higher',
  },
  'fixed assets net': {
    formula: 'Cost basis − accumulated depreciation',
    hint: 'Property, equipment, vehicles (net book value).',
    explanation: 'NC-Assets at net book value across the period. Driven by Command simulations (buy/sell) when enabled.',
    betterWhen: 'neutral',
  },
  'equity': {
    formula: 'Total assets − Total liabilities',
    hint: 'Residual claim after liabilities.',
    explanation: 'Total equity across the period. Rises with saved income and debt paydown; falls with drawdowns and new debt.',
    betterWhen: 'higher',
  },
  'net fcf': {
    formula: 'CFO + CFI + CFF',
    hint: 'Free cash movement for the period.',
    explanation: 'Net free cash flow — change in liquid position from one period to the next. Positive = net saver; negative = net spender.',
    betterWhen: 'higher',
  },
  'net income': {
    formula: 'Revenue − Operating − Non-operating',
    hint: 'Bottom line per period.',
    explanation: 'Net income per period. Positive = accumulating equity; negative = drawing it down.',
    direction: { whenUp: 'Profitability improving.', whenDown: 'Profitability eroding.' },
    betterWhen: 'higher',
  },

  /* ── Income Statement KPI timeline labels ───────────────────── */
  'revenue (primary income + recurring)': {
    formula: 'Primary income gross + recurring revenue',
    hint: 'Combined top-line revenue.',
    explanation: 'Monthly primary income plus recurring revenue. Core revenue signal.',
    betterWhen: 'higher',
  },
  'subscriptions expense': {
    hint: 'Monthly subscription outflow.',
    explanation: 'Total subscription spend per period (SaaS, streaming, memberships) — the recurring burn.',
    betterWhen: 'lower',
  },
  'support ap expense': {
    hint: 'Support outflow.',
    explanation: 'Combined support AP per period — direct + routed channel.',
    betterWhen: 'neutral',
  },
  'wht + gov': {
    hint: 'Tax + statutory contributions.',
    explanation: 'Withholding tax plus statutory contributions. Fixed overhead on earned income.',
    betterWhen: 'neutral',
  },

  /* ── Cash Flow KPI timeline labels ──────────────────────────── */
  'cfo (operating)': {
    formula: 'Net income + non-cash adjustments',
    hint: 'Operating cash flow.',
    explanation: 'Net cash generated from operations each period. Should be positive in steady state.',
    betterWhen: 'higher',
  },
  'net change in cash': {
    formula: 'CFO + CFI + CFF',
    hint: 'Liquid movement for the period.',
    explanation: 'Net change in cash across all three sections (Operating / Investing / Financing) — equals the change in liquid assets on the BS.',
    betterWhen: 'higher',
  },
  'gross inflow': {
    hint: 'Total income inflow.',
    explanation: 'Gross income flowing in per period (primary income + recurring revenue + AR receipts). Before any expenses or tax.',
    betterWhen: 'higher',
  },
  'base burn': {
    hint: 'Base spending level.',
    explanation: 'Baseline monthly burn rate excluding overlays (fuel / seasonal / lifestyle / subscription reinvestment).',
    betterWhen: 'lower',
  },

  /* ── Working Capital / Drivers ──────────────────────────────── */
  'recurring subs (all)': {
    hint: 'All recurring subscriptions.',
    explanation: 'All subscriptions (monthly + amortized yearly + amortized quarterly) as a combined monthly-equivalent.',
    betterWhen: 'lower',
  },
  'support ap total': {
    hint: 'Support AP across streams.',
    explanation: 'Total monthly support AP across direct + routed channels.',
    betterWhen: 'neutral',
  },
  'mortgage interest': {
    hint: 'Interest on mortgage payments.',
    explanation: 'Interest portion of mortgage payments (principal flows through CFF).',
    betterWhen: 'lower',
  },
  'recurring revenue': {
    hint: 'Predictable recurring revenue.',
    explanation: 'Recurring revenue. Grows with active units, pricing, and retention.',
    betterWhen: 'higher',
  },
  'primary income net': {
    formula: 'Gross - WHT - statutory contributions',
    hint: 'Take-home primary income.',
    explanation: 'Net primary income after tax and statutory contributions - what actually hits the bank.',
    betterWhen: 'higher',
  },

  /* ── Ratios + Profitability KPIs ────────────────────────────── */
  'gross margin %': {
    formula: '(Revenue − COGS) / Revenue',
    hint: 'Profit retention per unit of revenue.',
    explanation: 'Percent of revenue retained after direct costs. Higher margin = more retained reporting currency available for operating expenses and profit.',
    betterWhen: 'higher',
  },
  'runway (months)': {
    formula: 'Liquid ÷ monthly burn',
    hint: 'Months of burn covered by cash.',
    explanation: 'How long available reserves would last at current burn rate. >12 months = comfortable; 6-12 = tight; <6 = constrained.',
    betterWhen: 'higher',
  },

  /* ── Overlays (Forecasts) ────────────────────────────────────── */
  'fuel': {
    hint: 'Fuel spending.',
    explanation: 'Fuel overlay per period — driven by DOE pump price × liters consumed.',
    betterWhen: 'lower',
  },
  'seasonal': {
    hint: 'Seasonal event spending.',
    explanation: 'Seasonal event outflow for the period (birthdays, holidays, anniversaries). Non-uniform across months.',
    betterWhen: 'neutral',
  },
  'lifestyle': {
    hint: 'Discretionary lifestyle spending.',
    explanation: 'Discretionary lifestyle overlay (dining, entertainment, hobbies). First lever when tightening the budget.',
    betterWhen: 'lower',
  },
};

const ALIASES = {
  /* Add KPI-label aliases here when labels are renamed without changing
     the underlying meaning. */
};

export function lookupKPIGlossary(label) {
  if (label == null) return null;
  /* If label is a ReactElement (e.g. <>DSO <EduTip/></>) we can't
     stringify it reliably — return null and let the card render plain.
     EduTip handles its own explanation in that case. */
  if (typeof label !== 'string') return null;
  const key = normalizeKey(label);
  const aliased = ALIASES[key] ?? key;
  return ENTRIES[aliased] ?? null;
}
